/**
 * Animation worker for receiver transforms.
 *
 * The provider installs this worker on animation-candidate video receivers
 * via `receiver.transform = new RTCRtpScriptTransform(worker, { trackId })`.
 * That installation is itself the marking — the provider only transforms
 * tracks it has already identified — so a track becomes active as soon as its
 * transform arrives here, without waiting on a message. The
 * `{ kind: 'animationTrack', ... }` messages remain for un-marking and for
 * transceiver recycling.
 *
 * Frames belonging to marked tracks are checked against the animation
 * protocol with strict sniffing: matching frames are consumed (they are not
 * real video and must not reach the decoder) and their embedded metadata is
 * emitted to the provider; all other frames pass through untouched so real
 * video tracks keep working.
 *
 * Events are emitted via `postMessage` with our usual event envelope
 * (`type: 'animation' | ...`); the provider receives them through
 * `addEventListener('message')`.
 *
 * @internal
 * @packageDocumentation
 */

import { AnimationFrameProcessor, sniffAnimationFrame } from './animation-frame-processor';
import type { AnimationTransformEvent } from './types';

interface UpdateTrackIdMessage {
  kind: 'updateTrackId';
  data: {
    oldTrackId: string;
    newTrackId: string;
  };
}

type AnimationTrackActionMessage = {
  kind: 'animationTrack';
  trackId: string;
  action: 'add' | 'remove';
};

declare const self: DedicatedWorkerGlobalScope & {
  onrtctransform?: ((event: RTCTransformEvent) => void) | null;
};

interface RTCTransformEvent extends Event {
  transformer: {
    readable: ReadableStream;
    writable: WritableStream;
    options?: unknown;
  };
}

/** Per-track processors, keyed by trackId (LiveKit server track SID). */
const processors = new Map<string, AnimationFrameProcessor>();

/** TrackIds currently treated as animation tracks. */
const animationTrackIds = new Set<string>();

/** Next trackId for transforms installed without an explicit trackId. */
let nextDirectTrackId = 1;

function postEvent(evt: AnimationTransformEvent, trackId: string): void {
  // Transfer protobuf payloads for zero-copy delivery
  const transfer: Transferable[] =
    'protobufData' in evt && evt.protobufData instanceof ArrayBuffer
      ? [evt.protobufData]
      : [];
  self.postMessage({ ...evt, trackId }, { transfer });
}

function getProcessor(trackId: string): AnimationFrameProcessor {
  let processor = processors.get(trackId);
  if (!processor) {
    processor = new AnimationFrameProcessor((evt) => postEvent(evt, trackId));
    processors.set(trackId, processor);
  }
  return processor;
}

function setupDecodePipeline(
  readableStream: ReadableStream,
  writableStream: WritableStream,
  trackId: string,
): void {
  readableStream
    .pipeThrough(
      new TransformStream<RTCEncodedVideoFrame, RTCEncodedVideoFrame>({
        transform(frame, controller) {
          // Animation-marked tracks: strict protocol sniffing, consume only
          // frames that match the animation protocol exactly.
          if (
            animationTrackIds.has(trackId) &&
            sniffAnimationFrame(new Uint8Array(frame.data))
          ) {
            getProcessor(trackId).processFrame(
              new Uint8Array(frame.data),
              frame.timestamp,
              true,
            );
            // Drop the frame: it is not real video and must not reach the
            // decoder.
            return;
          }

          // Everything else passes through untouched.
          controller.enqueue(frame);
        },
      }),
    )
    .pipeTo(writableStream)
    .catch((err: unknown) => {
      postEvent(
        { type: 'error', error: `Animation transform pipe error: ${err}` },
        trackId,
      );
    });
}

self.onmessage = (
  event: MessageEvent<UpdateTrackIdMessage | AnimationTrackActionMessage>,
) => {
  const msg = event.data;

  try {
    if (msg.kind === 'updateTrackId') {
      // Transceiver recycling: remap processor + marking state.
      const { oldTrackId, newTrackId } = msg.data;
      const processor = processors.get(oldTrackId);
      if (processor) {
        processors.delete(oldTrackId);
        processors.set(newTrackId, processor);
      }
      if (animationTrackIds.has(oldTrackId)) {
        animationTrackIds.delete(oldTrackId);
        animationTrackIds.add(newTrackId);
      }
      return;
    }

    if (msg.kind === 'animationTrack') {
      if (msg.action === 'add') {
        animationTrackIds.add(msg.trackId);
      } else {
        animationTrackIds.delete(msg.trackId);
      }
      return;
    }
  } catch (err) {
    self.postMessage({
      type: 'error',
      error: `Animation worker error: ${err}`,
    });
  }
};

// The provider assigns
// `receiver.transform = new RTCRtpScriptTransform(worker, { trackId })`,
// which delivers the receiver's encoded frame streams here.
self.onrtctransform = (event) => {
  const transformer = event.transformer;
  const options = (transformer.options ?? {}) as { trackId?: string };
  const trackId = options.trackId ?? `direct-${nextDirectTrackId++}`;

  // Marked here rather than waiting for the provider's `animationTrack`
  // message. The provider only installs this transform on tracks it has
  // already decided carry animation, and it passes that decision in as
  // `options.trackId` — so arriving here *is* the marking, and asking the
  // main thread to confirm it again only adds a window to lose frames in.
  //
  // That window is real: on Safari the first frames reach this worker before
  // `TrackSubscribed` fires on the main thread, so the message that would set
  // the flag has not been sent yet and every early frame is passed through
  // unparsed. Chrome happens to deliver frames later and never showed it.
  animationTrackIds.add(trackId);

  setupDecodePipeline(transformer.readable, transformer.writable, trackId);
};

export {};
